import { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { isEmpty } from 'lodash';
import {
  useNowEstablishPageTypeStore,
  useEstablishStore,
  useIsPrevPageConfirmStore,
  getBtnProps,
  TransitionBtn,
  reloadHandler,
} from 'views/components/pages/accountEstablishment/hooks';
import {
  EstablishPageType,
  advanceQuestionInitialValues,
  advanceQuestionOnSubmit,
  AdvanceQuestionFormikType,
  advanceQuestionValidation,
  formErrorScroll,
  scrollToTop,
  AccountEstablishRequestParam,
} from 'views/utils/accountEstablish';
import { useNavigate } from 'react-router-dom';
import { ButtonProps } from 'views/components/atoms/button';
import { useGetDateTime } from 'views/utils/getDateTime';
import { AgreementDocumentInfo, useGetAgreementDocumentListQuery } from 'views/utils/graphql/graphql';
import { OpenAccountParamKey } from 'views/constants/const/AccountOpen';
import { localStorage } from 'views/utils/localStrage/localStrageUtil';

export const useAdvanceQuestion = () => {
  const [currentDateTime, setCurrentDateTime] = useState<string>('');
  const navigate = useNavigate();
  const { nowEstablishPageType, setNowEstablishPageType } = useNowEstablishPageTypeStore();
  const { isPrevPageConfirm, setIsPrevPageConfirm } = useIsPrevPageConfirmStore();

  const { dateTime, getDataTime } = useGetDateTime();
  const { param, add } = useEstablishStore();
  const initialValues = advanceQuestionInitialValues(param, currentDateTime);
  const onSubmit = advanceQuestionOnSubmit(param, currentDateTime, setNowEstablishPageType, add);

  const [agreementDocuments, getAgreementDocuments] = useGetAgreementDocumentListQuery({
    variables: {},
    requestPolicy: 'network-only',
    pause: true,
  });

  const formik: AdvanceQuestionFormikType = useFormik({
    initialValues,
    validationSchema: advanceQuestionValidation,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit,
  });

  const nextButtonProps: ButtonProps = getBtnProps(TransitionBtn.NEXT);
  const prevButtonProps: ButtonProps = getBtnProps(TransitionBtn.PREV, () => navigate(EstablishPageType.Login));

  // localStorage → store へのロード関数
  const loadOpenAccount = () => {
    if (typeof window === 'undefined') return;
    const saved = localStorage.get<AccountEstablishRequestParam>(OpenAccountParamKey);
    if (!saved) return;
    add(saved);
    formik.setValues(advanceQuestionInitialValues(saved, currentDateTime));
  };

  useEffect(() => {
    if (!dateTime.data?.GetDateTime?.Current) return;
    setCurrentDateTime(dateTime.data?.GetDateTime?.Current);
  }, [dateTime]);

  useEffect(() => {
    if (nowEstablishPageType === EstablishPageType.AddvanceQuestion) return;
    navigate(nowEstablishPageType.toString());
  }, [nowEstablishPageType]);

  useEffect(() => {
    if (formik.values.electronicDocumentAgreement) {
      getDataTime();
    }
  }, [formik.values.electronicDocumentAgreement]);

  useEffect(() => {
    if (isEmpty(formik.errors)) return;
    formErrorScroll();
  }, [formik.isSubmitting]);

  useEffect(() => {
    setIsPrevPageConfirm(false);
    scrollToTop();
    window.addEventListener('beforeunload', reloadHandler);
    return () => {
      window.removeEventListener('beforeunload', reloadHandler);
    };
  }, []);

  useEffect(() => {
    getAgreementDocuments();
  }, []);

  return {
    formik,
    buttonProps: {
      nextButtonProps,
      prevButtonProps,
    },
    getDataTime,
    agreementDocuments: (agreementDocuments.data?.GetAgreementDocumentList?.Data as AgreementDocumentInfo[]) ?? [],
    loadOpenAccount,
  };
};
